/**
 * TrayContext — Global Tray State Provider
 * ─────────────────────────────────────────
 * Owns the state that must persist across route navigations:
 *   - branches         → curated collections
 *   - scrubber         → journey/lineage chain
 *   - starred IDs      → user's starred projects
 *   - recent IDs       → last 5 viewed projects
 *
 * State that stays LOCAL to Dashboard (map-specific):
 *   - useSelection()   → highlighted IDs, multi-select mode
 *   - useFilters()     → zone filters, search, sort
 *   - useMapViewport() → zoom, pan
 *   - useOverlays()    → which overlays are open
 *   - useLineage()     → lineage computation
 *
 * v2 Changes:
 *  - Added renameBranch() — updates API + local state in one call
 *  - Added updateBranchProjects() — sends full project ID list to API + updates local state
 *  - Fixed saveBranchEdits to use projectsAPI.updateBranch (not nonexistent updateCuratedBranch)
 *  - Added refetchBranches() for manual refresh when needed
 *
 * Place at: src/context/TrayContext.jsx
 */

import React, {
  createContext, useContext, useState, useCallback, useEffect, useMemo,
} from 'react';
import toast from 'react-hot-toast';
import { projectsAPI, api } from '../api';
import { useAuth } from './AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// CONTEXT
// ─────────────────────────────────────────────────────────────────────────────

const TrayContext = createContext(null);

// ─────────────────────────────────────────────────────────────────────────────
// PROVIDER
// ─────────────────────────────────────────────────────────────────────────────

export function TrayProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const userId = user?.id;

  // ════════════════════════════════════════════════════════════════════════════
  // BRANCHES
  // ════════════════════════════════════════════════════════════════════════════

  const [myBranches, setMyBranches]           = useState([]);
  const [featuredBranches, setFeaturedBranches] = useState([]);
  const [activeBranch, setActiveBranch]       = useState(null);

  // ── Fetch branches ─────────────────────────────────────────────────────

  const fetchBranches = useCallback(async () => {
    if (!isAuthenticated || !userId) return;

    try {
      const res = await (projectsAPI.getMyBranches
        ? projectsAPI.getMyBranches()
        : api.get('/projects/my-curated-branches'));
      setMyBranches(res.data?.branches || res.data || []);
    } catch (err) {
      console.warn('Could not fetch user branches:', err.message);
    }

    try {
      const res = await (projectsAPI.getFeaturedBranches
        ? projectsAPI.getFeaturedBranches()
        : api.get('/projects/featured-curated-branches'));
      setFeaturedBranches(res.data?.branches || res.data || []);
    } catch (err) {
      console.warn('Could not fetch featured branches:', err.message);
    }
  }, [isAuthenticated, userId]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  // ── Select / deselect ──────────────────────────────────────────────────

  const selectBranch = useCallback((branch) => {
    setActiveBranch(prev => (prev?.id === branch.id ? null : branch));
  }, []);

  const clearBranch = useCallback(() => {
    setActiveBranch(null);
  }, []);

  // ── Edit mode ──────────────────────────────────────────────────────────

  const editBranch = useCallback((branch) => {
    setActiveBranch(branch);
    toast.success(
      `Editing "${branch.name || 'Branch'}". Shift+click to add/remove projects.`,
      { duration: 3000 }
    );
    return branch.curatedProjectIds || [];
  }, []);

  // ── Rename branch — API + local state ──────────────────────────────────

  const renameBranch = useCallback(async (branchId, newName) => {
    try {
      await projectsAPI.updateBranch(branchId, { name: newName });

      // Update local state immediately
      setMyBranches(prev =>
        prev.map(b => b.id === branchId ? { ...b, name: newName } : b)
      );
      setFeaturedBranches(prev =>
        prev.map(b => b.id === branchId ? { ...b, name: newName } : b)
      );
      setActiveBranch(prev =>
        prev?.id === branchId ? { ...prev, name: newName } : prev
      );

      toast.success('Collection renamed');
      return true;
    } catch (err) {
      console.error('Failed to rename branch:', err);
      toast.error('Could not rename collection');
      return false;
    }
  }, []);

  // ── Update branch projects — sends full ID list to API ─────────────────

  const updateBranchProjects = useCallback(async (branchId, projectIds) => {
    try {
      const newIds = [...projectIds];
      await projectsAPI.updateBranch(branchId, { curated_project_ids: newIds });

      // Update local state immediately
      setMyBranches(prev =>
        prev.map(b =>
          b.id === branchId
            ? { ...b, curatedProjectIds: newIds, curatedCount: newIds.length }
            : b
        )
      );
      setFeaturedBranches(prev =>
        prev.map(b =>
          b.id === branchId
            ? { ...b, curatedProjectIds: newIds, curatedCount: newIds.length }
            : b
        )
      );
      setActiveBranch(prev =>
        prev?.id === branchId
          ? { ...prev, curatedProjectIds: newIds, curatedCount: newIds.length }
          : prev
      );

      toast.success('Collection updated');
      return newIds;
    } catch (err) {
      console.error('Failed to update branch projects:', err);
      toast.error('Could not update collection');
      return null;
    }
  }, []);

  // ── Save edits (used by Dashboard multi-select flow) ───────────────────

  const saveBranchEdits = useCallback(async (currentMultiSelectedIds, branchId) => {
    const targetBranch = branchId
      ? [...myBranches, ...featuredBranches].find(b => b.id === branchId) || activeBranch
      : activeBranch;
    if (!targetBranch) return null;

    try {
      const newIds = [...currentMultiSelectedIds];
      await projectsAPI.updateBranch(targetBranch.id, { curated_project_ids: newIds });

      setMyBranches(prev =>
        prev.map(b =>
          b.id === targetBranch.id
            ? { ...b, curatedProjectIds: newIds, curatedCount: newIds.length }
            : b
        )
      );
      setActiveBranch(prev =>
        prev?.id === targetBranch.id
          ? { ...prev, curatedProjectIds: newIds, curatedCount: newIds.length }
          : prev
      );

      toast.success('Branch updated!');
      return newIds;
    } catch (err) {
      console.error('Failed to save branch:', err);
      toast.error('Failed to save branch');
      return null;
    }
  }, [activeBranch, myBranches, featuredBranches]);

  // ── Delete ─────────────────────────────────────────────────────────────

  const deleteBranch = useCallback(async (branch) => {
    if (!window.confirm(`Delete branch "${branch.name || 'Untitled'}"?`)) return false;
    try {
      await (projectsAPI.deleteBranch
        ? projectsAPI.deleteBranch(branch.id)
        : api.delete(`/projects/curated-branch/${branch.id}`));

      setMyBranches(prev => prev.filter(b => b.id !== branch.id));

      if (activeBranch?.id === branch.id) {
        setActiveBranch(null);
      }
      toast.success('Branch deleted');
      return true;
    } catch (err) {
      toast.error('Failed to delete branch');
      return false;
    }
  }, [activeBranch]);

  // ── Add new branch to local state ──────────────────────────────────────

  const addBranch = useCallback((branch) => {
    if (branch) setMyBranches(prev => [branch, ...prev]);
  }, []);

  // ── Branches API object ────────────────────────────────────────────────

  const branches = useMemo(() => ({
    myBranches,
    featuredBranches,
    activeBranch,
    setActiveBranch,
    selectBranch,
    clearBranch,
    editBranch,
    renameBranch,
    updateBranchProjects,
    saveBranchEdits,
    deleteBranch,
    addBranch,
    refetchBranches: fetchBranches,
  }), [
    myBranches, featuredBranches, activeBranch,
    selectBranch, clearBranch, editBranch, renameBranch, updateBranchProjects,
    saveBranchEdits, deleteBranch, addBranch, fetchBranches,
  ]);

  // ════════════════════════════════════════════════════════════════════════════
  // SCRUBBER (lifted from useScrubber.js)
  // ════════════════════════════════════════════════════════════════════════════

  const [showScrubber, setShowScrubber]       = useState(false);
  const [scrubberLineage, setScrubberLineage] = useState([]);

  const openScrubber = useCallback((lineageChain) => {
    if (lineageChain && lineageChain.length >= 1) {
      setScrubberLineage(lineageChain);
      setShowScrubber(true);
    }
  }, []);

  const closeScrubber = useCallback(() => {
    setShowScrubber(false);
  }, []);

  const scrubber = useMemo(() => ({
    showScrubber,
    scrubberLineage,
    openScrubber,
    closeScrubber,
    setShowScrubber,
    setScrubberLineage,
  }), [showScrubber, scrubberLineage, openScrubber, closeScrubber]);

  // ════════════════════════════════════════════════════════════════════════════
  // STARRED (lifted from useFilters.js)
  // ════════════════════════════════════════════════════════════════════════════

  const [starredIds, setStarredIds] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const res = await projectsAPI.getStarred?.() || await projectsAPI.search?.('', {});
        if (res?.data?.starredIds) setStarredIds(res.data.starredIds);
      } catch (err) {
        console.warn('Could not fetch starred projects:', err.message);
      }
    })();
  }, [isAuthenticated]);

  const starProject = useCallback(async (projectId) => {
    try {
      await projectsAPI.star(projectId);
      setStarredIds(prev => [...new Set([...prev, projectId])]);
    } catch (err) {
      console.error('Failed to star project:', err);
      toast.error('Failed to star project');
    }
  }, []);

  const unstarProject = useCallback(async (projectId) => {
    try {
      await projectsAPI.unstar(projectId);
      setStarredIds(prev => prev.filter(id => id !== projectId));
    } catch (err) {
      console.error('Failed to unstar project:', err);
      toast.error('Failed to unstar project');
    }
  }, []);

  const starred = useMemo(() => ({
    starredIds,
    starProject,
    unstarProject,
  }), [starredIds, starProject, unstarProject]);

  // ════════════════════════════════════════════════════════════════════════════
  // RECENT PROJECTS (lifted from Dashboard local state)
  // ════════════════════════════════════════════════════════════════════════════

  const [recentProjectIds, setRecentProjectIds] = useState([]);

  const recordRecentProject = useCallback((project) => {
    if (!project?.id) return;
    setRecentProjectIds(prev => {
      const next = [project.id, ...prev.filter(id => id !== project.id)];
      return next.slice(0, 5);
    });
  }, []);

  const recent = useMemo(() => ({
    recentProjectIds,
    recordRecentProject,
  }), [recentProjectIds, recordRecentProject]);

  // ════════════════════════════════════════════════════════════════════════════
  // SEARCH PROJECTS (shared utility for SelectionTray on any page)
  // ════════════════════════════════════════════════════════════════════════════

  const searchProjects = useCallback(async (query) => {
    try {
      const res = await projectsAPI.getAll({ search: query, limit: 10 });
      return res.data.projects || res.data || [];
    } catch { return []; }
  }, []);

  // ════════════════════════════════════════════════════════════════════════════
  // FOCUSED PROJECT IDS — highlights a single project + its lineage on any page
  // Set by tray interactions (clicking a project in a branch card).
  // Separate from branch highlighting (which dims everything NOT in the branch).
  // ════════════════════════════════════════════════════════════════════════════

  const [focusedProjectIds, setFocusedProjectIds] = useState(null); // null = no focus, Set = focused

  const focusProject = useCallback((ids) => {
    setFocusedProjectIds(ids ? new Set(Array.isArray(ids) ? ids : ids) : null);
  }, []);

  const clearFocus = useCallback(() => {
    setFocusedProjectIds(null);
  }, []);

  const focus = useMemo(() => ({
    focusedProjectIds,
    focusProject,
    clearFocus,
  }), [focusedProjectIds, focusProject, clearFocus]);

  // ════════════════════════════════════════════════════════════════════════════
  // CONTEXT VALUE
  // ════════════════════════════════════════════════════════════════════════════

  const value = useMemo(() => ({
    branches,
    scrubber,
    starred,
    recent,
    searchProjects,
    focus,
  }), [branches, scrubber, starred, recent, searchProjects, focus]);

  return (
    <TrayContext.Provider value={value}>
      {children}
    </TrayContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CONSUMER HOOK
// ─────────────────────────────────────────────────────────────────────────────

export function useTray() {
  const ctx = useContext(TrayContext);
  if (!ctx) {
    throw new Error('useTray must be used within a TrayProvider');
  }
  return ctx;
}

export default TrayContext;